/**
 * Execute LLM-generated Office.js code within a Word.run/Excel.run/etc context.
 *
 * The generated code receives `context` (RequestContext) as a parameter,
 * plus `Word`, `Excel`, `PowerPoint`, and `Office` globals so LLMs can
 * reference enums like `Word.InsertLocation.end` or `Word.BuiltInStyleName.*`.
 *
 * Uses `new Function()` to compile the code — no strict-mode sandboxing
 * to avoid conflicts with reserved words as parameter names.
 */
export async function executeSandboxed(
  code: string,
  context: unknown
): Promise<string> {
  try {
    // Expose Office app globals that LLMs commonly reference.
    // These are globally available in Office Add-ins but `new Function()`
    // runs in a clean scope, so we must pass them explicitly.
    const globals: Record<string, unknown> = { context };

    // Add Office globals if they exist
    const g = globalThis as Record<string, unknown>;
    if (g.Word) globals.Word = g.Word;
    if (g.Excel) globals.Excel = g.Excel;
    if (g.PowerPoint) globals.PowerPoint = g.PowerPoint;
    if (g.Outlook) globals.Outlook = g.Outlook;
    if (g.Office) globals.Office = g.Office;
    if (g.OfficeExtension) globals.OfficeExtension = g.OfficeExtension;

    const paramNames = Object.keys(globals);
    const paramValues = Object.values(globals);

    const fn = new Function(
      ...paramNames,
      `return (async function(${paramNames.join(", ")}) { ${code} })(${paramNames.join(", ")});`
    );
    const result = await fn(...paramValues);
    if (result === undefined || result === null) return "Done";
    if (typeof result === "string") return result;
    return JSON.stringify(result);
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error);
    throw new Error(`Office.js execution failed: ${errorMsg}`);
  }
}
